import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Button, Space, Typography, Alert, Tag, Avatar, Badge, Modal } from 'antd';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  StarOutlined, 
  ClockCircleOutlined, 
  MoneyCollectOutlined,
  PlusOutlined,
  BankOutlined,
  FileTextOutlined,
  BarChartOutlined,
  ThunderboltOutlined,
  SwapRightOutlined,
  UserOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { useProjects } from '../contexts/ProjectContext';
import { useI18n } from '../i18n/I18nContext';
import { getRecentInvoices, getPaidInvoices, Invoice as InvoiceType, getLedgerEntries, LedgerEntry } from '../firebase/firestore';
import { ledgerApi } from '../api/ledger';
import { ratesApi, ExchangeRates } from '../api/rates';
import CreateInvoiceForm from '../components/CreateInvoiceForm';
import HeaderDateRange, { lastDays } from '../components/HeaderDateRange';
import type { DateRange } from '../components/HeaderDateRange';
import './Dashboard.css';

const { Title, Paragraph, Text } = Typography;

interface Balance {
  total: number;
  availableStars: number;
  pendingStars: number;
  waitingStars: number;
  lifetimeEarnedStars: number;
}

interface Invoice {
  id: string;
  starsAmount: number;
  status: string;
  createdAt: any;
}

type Currency = 'STARS' | 'EUR' | 'USD' | 'TON';

const Dashboard: React.FC = () => {
  const { user, firebaseUser } = useAuth();
  const { includesInvoice, selectedProject } = useProjects();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [balance, setBalance] = useState<Balance | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModalSuccess, setCreateModalSuccess] = useState(false);
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [paidInvoices, setPaidInvoices] = useState<InvoiceType[]>([]);
  const [recentActivities, setRecentActivities] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<Currency>('STARS');
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  // Emplacement dans le header de l'app (Layout) où est rendu le sélecteur de période (portail)
  const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHeaderSlot(document.getElementById('app-header-slot'));
  }, []);
  // Période analysée (sélecteur calendrier du header) : par défaut les 7 derniers jours
  const [dateRange, setDateRange] = useState<DateRange>(() => lastDays(7));
  
  const TON_RATE = 105;

  useEffect(() => {
    if (user && firebaseUser) {
      loadBalance();
      loadRecentInvoices();
      loadPaidInvoices();
      loadExchangeRates();
      loadRecentActivities();
    }
  }, [user, firebaseUser]);

  const loadExchangeRates = async () => {
    try {
      const rates = await ratesApi.getCurrentRates();
      setExchangeRates(rates);
    } catch (err: any) {
      console.error('Error loading exchange rates:', err);
      setExchangeRates({
        EUR: { starsPerUnit: 46.38 },
        USD: { starsPerUnit: 42.50 },
      });
    }
  };

  const convertAmount = (starsAmount: number): { value: number; suffix: string } => {
    if (currency === 'STARS') {
      return { value: starsAmount, suffix: t.app.common.stars };
    }
    
    if (!exchangeRates) {
      return { value: starsAmount, suffix: t.app.common.stars };
    }

    switch (currency) {
      case 'EUR':
        const eurValue = parseFloat((starsAmount / exchangeRates.EUR.starsPerUnit).toFixed(2));
        return { value: eurValue, suffix: '€' };
      case 'USD':
        const usdValue = parseFloat((starsAmount / exchangeRates.USD.starsPerUnit).toFixed(2));
        return { value: usdValue, suffix: '$' };
      case 'TON':
        const tonValue = parseFloat((starsAmount / TON_RATE).toFixed(4));
        return { value: tonValue, suffix: 'TON' };
      default:
        return { value: starsAmount, suffix: t.app.common.stars };
    }
  };

  const loadBalance = async () => {
    if (!firebaseUser) return;
    
    try {
      const balanceData = await ledgerApi.getBalance();
      setBalance({
        total: balanceData.total || 0,
        availableStars: balanceData.availableStars || 0,
        pendingStars: balanceData.pendingStars || 0,
        waitingStars: balanceData.waitingStars || 0,
        lifetimeEarnedStars: balanceData.lifetimeEarnedStars || 0,
      });
      setError(null);
    } catch (err: any) {
      setError(err.message || t.app.dashboard.failedLoadBalance);
    }
  };

  const loadRecentInvoices = async () => {
    if (!firebaseUser) return;
    
    try {
      const invoices = await getRecentInvoices(firebaseUser.uid, 5);
      setRecentInvoices(invoices);
    } catch (err: any) {
      console.error('Error loading recent invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPaidInvoices = async () => {
    if (!firebaseUser) return;
    
    try {
      const paid = await getPaidInvoices(firebaseUser.uid, 10);
      setPaidInvoices(paid);
    } catch (err: any) {
      console.error('Error loading paid invoices:', err);
    }
  };

  const loadRecentActivities = async () => {
    if (!firebaseUser) return;
    
    try {
      const activities = await getLedgerEntries(firebaseUser.uid, undefined, 100);
      setRecentActivities(activities);
    } catch (err: any) {
      console.error('Error loading recent activities:', err);
    }
  };

  const formatTimeAgo = (date: Date): string => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}${t.app.common.daysShort}`;
    if (hours > 0) return `${hours}${t.app.common.hoursShort}`;
    if (minutes > 0) return `${minutes}${t.app.common.minutesShort}`;
    return t.app.common.justNow;
  };

  const scopedPaidInvoices = useMemo(
    () => paidInvoices.filter((invoice) => includesInvoice(invoice.id)),
    [paidInvoices, includesInvoice],
  );
  const scopedRecentInvoices = useMemo(
    () => recentInvoices.filter((invoice) => includesInvoice(invoice.id)),
    [recentInvoices, includesInvoice],
  );
  const scopedActivities = useMemo(
    () => recentActivities.filter((entry) => (entry.invoiceId ? includesInvoice(entry.invoiceId) : !selectedProject)),
    [recentActivities, includesInvoice, selectedProject],
  );

  // Calculate balance change from last month (comparing current month vs last month earnings)
  const balanceChange = useMemo(() => {
    if (!scopedPaidInvoices || scopedPaidInvoices.length === 0) return null;

    const now = new Date();
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const twoMonthsAgo = new Date(now);
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);

    // Calculate total earnings from last month (between 1 and 2 months ago)
    let lastMonthEarnings = 0;
    // Calculate total earnings from current month (last 30 days)
    let currentMonthEarnings = 0;

    scopedPaidInvoices.forEach((invoice) => {
      if (invoice.paidAt) {
        const paidDate = invoice.paidAt instanceof Date ? invoice.paidAt : new Date(invoice.paidAt);
        const amount = invoice.netAmount || 0;
        
        if (paidDate >= oneMonthAgo) {
          // Current month
          currentMonthEarnings += amount;
        } else if (paidDate >= twoMonthsAgo) {
          // Last month
          lastMonthEarnings += amount;
        }
      }
    });

    // Calculate percentage change
    if (lastMonthEarnings === 0) return null;
    
    const change = ((currentMonthEarnings - lastMonthEarnings) / lastMonthEarnings) * 100;
    return {
      percentage: Math.abs(change).toFixed(1),
      isPositive: change >= 0,
    };
  }, [scopedPaidInvoices]);

  // Bornes de la période sélectionnée (ms) et période précédente de même durée
  const [rangeStart, rangeEnd] = dateRange;
  const rangeStartMs = rangeStart.valueOf();
  const rangeEndMs = rangeEnd.valueOf();
  const rangeDays = rangeEnd.startOf('day').diff(rangeStart.startOf('day'), 'day') + 1;

  // Prepare chart data from ledger entries (GROSS type = stars earned), un point par jour de la période
  const chartData = useMemo(() => {
    if (!scopedActivities || scopedActivities.length === 0) return [];

    interface DayData {
      key: string;
      day: string;
      amount: number;
    }
    const days: DayData[] = [];
    const byKey = new Map<string, DayData>();
    for (let i = 0; i < rangeDays; i++) {
      const date = rangeStart.add(i, 'day');
      const d: DayData = {
        key: date.format('YYYY-MM-DD'),
        // Sur de longues périodes on affiche jour/mois pour rester lisible
        day: rangeDays > 31 ? date.format('D/M') : date.format('D'),
        amount: 0,
      };
      days.push(d);
      byKey.set(d.key, d);
    }

    // Filter ledger entries by period (GROSS type = payments received) and sum amounts by day
    scopedActivities.forEach((entry) => {
      if (entry.type === 'GROSS' && entry.amountStars > 0) {
        const entryDate = entry.createdAt instanceof Date ? entry.createdAt : new Date(entry.createdAt);
        const ts = entryDate.getTime();
        if (ts >= rangeStartMs && ts <= rangeEndMs) {
          const target = byKey.get(dayjs(entryDate).format('YYYY-MM-DD'));
          if (target) target.amount += entry.amountStars;
        }
      }
    });

    // Convert amounts based on current currency
    return days.map((d) => {
      const converted = convertAmount(d.amount);
      return {
        day: d.day,
        amount: converted.value,
        amountRaw: d.amount,
      };
    });
  }, [scopedActivities, currency, exchangeRates, rangeStart, rangeStartMs, rangeEndMs, rangeDays]);

  // Filter paid invoices by period for total calculation
  const filteredPaidInvoices = useMemo(() => {
    if (!scopedPaidInvoices || scopedPaidInvoices.length === 0) return [];
    return scopedPaidInvoices.filter((invoice) => {
      if (!invoice.paidAt) return false;
      const paidDate = invoice.paidAt instanceof Date ? invoice.paidAt : new Date(invoice.paidAt);
      const ts = paidDate.getTime();
      return ts >= rangeStartMs && ts <= rangeEndMs;
    });
  }, [scopedPaidInvoices, rangeStartMs, rangeEndMs]);

  // Variation du total encaissé vs la période précédente de même durée
  const salesChange = useMemo(() => {
    if (!scopedPaidInvoices || scopedPaidInvoices.length === 0) return null;
    const prevStart = rangeStartMs - rangeDays * 24 * 60 * 60 * 1000;
    let previous = 0;
    scopedPaidInvoices.forEach((invoice) => {
      if (!invoice.paidAt) return;
      const ts = (invoice.paidAt instanceof Date ? invoice.paidAt : new Date(invoice.paidAt)).getTime();
      if (ts >= prevStart && ts < rangeStartMs) previous += invoice.netAmount || 0;
    });
    const current = filteredPaidInvoices.reduce((sum, inv) => sum + (inv.netAmount || 0), 0);
    if (previous === 0) return null;
    const change = ((current - previous) / previous) * 100;
    return { percentage: Math.abs(change).toFixed(1), isPositive: change >= 0 };
  }, [scopedPaidInvoices, filteredPaidInvoices, rangeStartMs, rangeDays]);

  const getActivityDescription = (entry: LedgerEntry): string => {
    if (entry.invoice) {
      const title = entry.invoice.title || entry.invoice.description || t.app.common.noTitle;
      return t.app.dashboard.paymentReceived.replace('{title}', title);
    }
    if (entry.withdraw) {
      const converted = convertAmount(entry.withdraw.amountStars);
      return t.app.dashboard.withdrawalRequested
        .replace('{amount}', String(converted.value))
        .replace('{suffix}', converted.suffix);
    }
    const converted = convertAmount(entry.amountStars);
    return t.app.dashboard.transaction
      .replace('{amount}', String(converted.value))
      .replace('{suffix}', converted.suffix);
  };

  if (loading) {
    return (
      <div className="dashboard-loading" role="status" aria-label={t.app.dashboard.loading}>
        <div className="dashboard-loader" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={t.app.common.error}
        description={error}
        type="error"
        showIcon
        closable
        onClose={() => setError(null)}
      />
    );
  }

  return (
    <div className="dashboard-modern-vykins">
      {/* Sélecteur de période (calendrier) : rendu dans le header de l'app (à gauche) via portail, repli inline sinon */}
      {(() => {
        const picker = <HeaderDateRange value={dateRange} onChange={setDateRange} />;
        return headerSlot ? createPortal(picker, headerSlot) : <div className="dashboard-period-selector">{picker}</div>;
      })()}

      {/* Main Content */}
      <div className="dashboard-content-vykins">
        {/* Left Sidebar - Navigation is handled by Layout component */}
        
        {/* Center Content */}
        <div className="dashboard-main-content">
          {/* Quick Actions - Scrollable */}
          <div className="quick-actions-section scrollable-section">
            <div className="section-content">
              <Title level={4} className="section-title">{t.app.dashboard.quickActions}</Title>
            <div className="quick-actions-row">
              <Card 
                className="action-card action-card-create" 
                hoverable
                onClick={() => setCreateModalOpen(true)}
                variant="borderless"
              >
                <div className="action-icon create">
                  <ThunderboltOutlined />
                </div>
                <div className="action-card-text">
                  <h3 className="action-card-title">{t.app.dashboard.createLink}</h3>
                  <p className="action-card-description">{t.app.dashboard.createLinkDesc}</p>
                </div>
              </Card>
              <Card 
                className="action-card action-card-withdraw" 
                hoverable
                onClick={() => navigate('/app/withdraws/new')}
                variant="borderless"
              >
                <div className="action-icon withdraw">
                  <BankOutlined />
                </div>
                <div className="action-card-text">
                  <h3 className="action-card-title">{t.app.dashboard.withdraw}</h3>
                  <p className="action-card-description">{t.app.dashboard.withdrawDesc}</p>
                </div>
              </Card>
              <Card 
                className="action-card action-card-invoices" 
                hoverable
                onClick={() => navigate('/app/invoices')}
                variant="borderless"
              >
                <div className="action-icon invoices">
                  <FileTextOutlined />
                </div>
                <div className="action-card-text">
                  <h3 className="action-card-title">{t.app.dashboard.invoices}</h3>
                  <p className="action-card-description">{t.app.dashboard.invoicesDesc}</p>
                </div>
              </Card>
              <Card 
                className="action-card action-card-ledger" 
                hoverable
                onClick={() => navigate('/app/ledger')}
                variant="borderless"
              >
                <div className="action-icon ledger">
                  <BarChartOutlined />
                </div>
                <div className="action-card-text">
                  <h3 className="action-card-title">{t.app.dashboard.ledger}</h3>
                  <p className="action-card-description">{t.app.dashboard.ledgerDesc}</p>
                </div>
              </Card>
            </div>
            </div>
          </div>

          {/* Overall Summary Card - Scrollable */}
          <div className="summary-section scrollable-section">
            <div className="section-content">
          <Card className="summary-card" variant="borderless">
            <div className="summary-header">
              <Title level={4} className="summary-title">{t.app.dashboard.overallSummary}</Title>
            </div>
            <Row gutter={24} className="summary-stats">
              <Col xs={24} sm={24} md={8}>
                <div className="stat-item">
                  <Text className="stat-label">{t.app.dashboard.balance}</Text>
                  <div className="stat-value">
                    {balance ? convertAmount(balance.total).value.toLocaleString() : '0'}
                    <span className="stat-suffix">{balance ? convertAmount(balance.total).suffix : t.app.common.stars}</span>
                  </div>
                  {balanceChange && (
                    <div className={`stat-change ${balanceChange.isPositive ? 'positive' : 'negative'}`}>
                      {balanceChange.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      {balanceChange.percentage}% {t.app.dashboard.fromLastMonth}
                    </div>
                  )}
                </div>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <div className="stat-item">
                  <Text className="stat-label">{t.app.dashboard.available}</Text>
                  <div className="stat-value">
                    {balance ? convertAmount(balance.availableStars).value.toLocaleString() : '0'}
                    <span className="stat-suffix">{balance ? convertAmount(balance.availableStars).suffix : t.app.common.stars}</span>
                  </div>
                </div>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <div className="stat-item">
                  <Text className="stat-label">{t.app.dashboard.totalEarned}</Text>
                  <div className="stat-value">
                    {balance ? convertAmount(balance.lifetimeEarnedStars).value.toLocaleString() : '0'}
                    <span className="stat-suffix">{balance ? convertAmount(balance.lifetimeEarnedStars).suffix : t.app.common.stars}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
            </div>
          </div>

          {/* Sales Overview / Recent Paid Invoices - Scrollable */}
          <div className="sales-section scrollable-section">
            <div className="section-content">
          {scopedPaidInvoices.length > 0 && (
            <Card className="sales-card" variant="borderless">
              <div className="sales-header">
                <Title level={4} className="sales-title">{t.app.dashboard.recentPayments}</Title>
                <Text className="sales-range-label">
                  {rangeStart.format('D MMM')} – {rangeEnd.format('D MMM YYYY')}
                </Text>
              </div>
              <div className="sales-content">
                <div className="sales-amount">
                  {convertAmount(filteredPaidInvoices.reduce((sum, inv) => sum + inv.netAmount, 0)).value.toLocaleString()}
                  <span className="sales-suffix">{convertAmount(filteredPaidInvoices.reduce((sum, inv) => sum + inv.netAmount, 0)).suffix}</span>
                </div>
                {salesChange && (
                  <div className={`sales-change ${salesChange.isPositive ? 'positive' : 'negative'}`}>
                    {salesChange.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {salesChange.percentage}%{' '}
                    {t.app.dashboard.vsPreviousPeriod ?? 'vs previous period'}
                  </div>
                )}
                {/* Chart */}
                <div className="sales-chart-container">
                  <ResponsiveContainer width="100%" height="100%" minHeight={140}>
                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <XAxis 
                        dataKey="day" 
                        tick={{ fill: 'rgba(26, 26, 26, 0.6)', fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          padding: '8px 12px',
                        }}
                        formatter={(value: number) => [
                          `${value.toFixed(0)} ${t.app.common.stars}`,
                          t.app.dashboard.chartAmount
                        ]}
                      />
                      <Bar 
                        dataKey="amount" 
                        fill="#ff9500"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={60}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </Card>
          )}
            </div>
          </div>

        </div>

        {/* Right Sidebar */}
        <div className="dashboard-sidebar-right">
          {/* Recent Activity - Scrollable */}
          <Card className="sidebar-card sidebar-card-scrollable" variant="borderless">
            <Title level={5} className="sidebar-title">{t.app.dashboard.recentActivity}</Title>
            <div className="activity-list">
              {scopedActivities.length > 0 ? (
                scopedActivities.map((activity) => (
                  <div key={activity.id} className="activity-item">
                    <Avatar size="small" icon={<UserOutlined />} className="activity-avatar" />
                    <div className="activity-content">
                      <div className="activity-name">
                        {activity.payerName || activity.payerUsername || t.app.common.system}
                      </div>
                      <div className="activity-description">
                        {getActivityDescription(activity)}
                      </div>
                      <div className="activity-time">
                        {formatTimeAgo(activity.createdAt)}
                      </div>
                    </div>
                    {activity.amountStars > 0 && (
                      <Badge status="success" className="activity-status" />
                    )}
                  </div>
                ))
              ) : (
                <div className="no-activity">{t.app.dashboard.noRecentActivity}</div>
              )}
            </div>
            <Button type="link" className="see-all-btn" onClick={() => navigate('/app/ledger')}>
              {t.app.common.seeAll}
            </Button>
          </Card>

          {/* Most Recent Products / Invoices - Scrollable */}
          <Card className="sidebar-card sidebar-card-scrollable" variant="borderless">
            <Title level={5} className="sidebar-title">{t.app.dashboard.mostRecent}</Title>
            <div className="products-list">
              {scopedRecentInvoices.length > 0 ? (
                scopedRecentInvoices.map((invoice) => (
                  <div key={invoice.id} className="product-item">
                    <div className="product-info">
                      <div className="product-name">{t.app.dashboard.invoiceLabel.replace('{id}', invoice.id.slice(0, 8))}</div>
                      <div className="product-price">
                        {convertAmount(invoice.starsAmount).value} {convertAmount(invoice.starsAmount).suffix}
                      </div>
                    </div>
                    <Tag color={invoice.status === 'PAID' ? 'success' : 'default'} className="product-status">
                      {invoice.status}
                    </Tag>
                  </div>
                ))
              ) : (
                <div className="no-products">{t.app.dashboard.noRecentInvoices}</div>
              )}
            </div>
            <Button type="link" className="see-all-btn" onClick={() => navigate('/app/invoices')}>
              {t.app.dashboard.allInvoices}
            </Button>
          </Card>
        </div>
      </div>

      <Modal
        open={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        footer={null}
        destroyOnClose
        width={560}
        className="create-invoice-modal"
        title={createModalSuccess ? t.app.createInvoice.successTitle : t.app.createInvoice.title}
      >
        <CreateInvoiceForm
          onCancel={() => setCreateModalOpen(false)}
          onViewInvoices={() => {
            setCreateModalOpen(false);
            navigate('/app/invoices');
          }}
          onSuccessViewChange={setCreateModalSuccess}
        />
      </Modal>
    </div>
  );
};

export default Dashboard;
